// Helpers around the engine's optional inputs: the QBID worksheet (computeQbid, Form 8995/8995-A context from the
// result's federal lines) and the per-party state tax adjustment.
import { computeQbid, type QbidContext, type QbidResult } from '@divorceos/engine';
import { toIrsStatus, type Case, type CaseResult, type FilingStatus, type PartyIndex, type PartyInputs, type QbidWorksheet } from '@divorceos/schema';

export type QbidCtx = QbidContext;
export type QbidOut = QbidResult;
export { computeQbid };

/** The QBID context for party p from the guideline federal lines of a result (annual). */
export function qbidContextFor(c: Case, r: CaseResult, p: PartyIndex): QbidCtx {
  const L = r.parties[p].guideline.federal.lines;
  return {
    taxableIncomeBeforeQbid: Math.max(0, (L['1040.15'] ?? 0) + (L['1040.13'] ?? 0)),
    netCapitalGain: Math.max(0, L['1040.7'] ?? 0),
    status: toIrsStatus(c.parties[p].filingStatus as FilingStatus),
    year: c.taxYear,
  };
}

export function emptyWorksheet(): QbidWorksheet {
  return { use: false, businesses: [], reitPtp: 0, lossCarryforward: 0 };
}

/** The party's worksheet (a fresh empty one when absent). */
export function worksheetOf(p: PartyInputs): QbidWorksheet {
  return p.qbidWorksheet ?? emptyWorksheet();
}

// ------------------------------------------------------------------ state tax adjustment

export interface StateTaxAdj { factorPercent: number; userMonthlyAmount: number | null }

export const stateAdjOf = (p: PartyInputs): StateTaxAdj => ({
  factorPercent: Number.isFinite(p.stateTaxAdjustment?.factorPercent) ? (p.stateTaxAdjustment?.factorPercent ?? 100) : 100,
  userMonthlyAmount: typeof p.stateTaxAdjustment?.userMonthlyAmount === 'number' && Number.isFinite(p.stateTaxAdjustment.userMonthlyAmount) ? p.stateTaxAdjustment.userMonthlyAmount : null,
});

export const isStateAdjusted = (a: StateTaxAdj): boolean => a.userMonthlyAmount !== null || a.factorPercent !== 100;
export const anyStateAdjusted = (c: Case): boolean => c.parties.some((p) => isStateAdjusted(stateAdjOf(p)));

/** Write an adjustment; the default (100%, computed) removes the field so the case stays as before. */
export function setStateAdj(p: PartyInputs, patch: Partial<StateTaxAdj>): void {
  const next = { ...stateAdjOf(p), ...patch };
  if (isStateAdjusted(next)) p.stateTaxAdjustment = next;
  else delete p.stateTaxAdjustment;
}

/** Short text for the banner and reports: "80%" or "$1,200/mo (user)". */
export function stateAdjText(a: StateTaxAdj): string {
  if (a.userMonthlyAmount !== null) return `$${Math.round(a.userMonthlyAmount).toLocaleString('en-US')}/mo (user)`;
  return `${a.factorPercent}%`;
}

// ------------------------------------------------------------------ federal tax adjustment (TAX-07)

export const federalAdjOf = (p: PartyInputs): StateTaxAdj => ({
  factorPercent: Number.isFinite(p.federalTaxAdjustment?.factorPercent) ? (p.federalTaxAdjustment?.factorPercent ?? 100) : 100,
  userMonthlyAmount: typeof p.federalTaxAdjustment?.userMonthlyAmount === 'number' && Number.isFinite(p.federalTaxAdjustment.userMonthlyAmount) ? p.federalTaxAdjustment.userMonthlyAmount : null,
});
export const isFederalAdjusted = (a: StateTaxAdj): boolean => a.userMonthlyAmount !== null || a.factorPercent !== 100;
export const anyFederalAdjusted = (c: Case): boolean => c.parties.some((p) => isFederalAdjusted(federalAdjOf(p)));

/** Write a federal adjustment; the default (100%, computed) removes the field so the case stays as before. */
export function setFederalAdj(p: PartyInputs, patch: Partial<StateTaxAdj>): void {
  const next = { ...federalAdjOf(p), ...patch };
  if (isFederalAdjusted(next)) p.federalTaxAdjustment = next;
  else delete p.federalTaxAdjustment;
}
